/*
Purpose: Create a Session Time template from the Calendar (v2) toolbar or empty slot.
*/

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import Button from '@mui/material/Button';
import Select from 'react-select';
import Moment from 'moment';

import {
  options_days_sessTime,
  options_holidays,
  options_isholiday,
} from '../../../global';
import { createSessionTime } from '../calendar-api';

const SESSION_TYPE_OPTIONS = [
  { value: '', label: '---Select---' },
  { value: 'Online', label: 'Online' },
  { value: 'In centre', label: 'In centre' },
];

const EMPTY_FORM = {
  fk_tut_key: '',
  sest_type: '',
  sest_is_holidays: '',
  sest_from_date: '',
  sest_to_date: '',
  sest_day: '',
  sest_start_time: '',
  sest_end_time: '',
  sest_skip_holidays: '',
  sest_max_student: '1',
  sest_description: '',
};

function dayNameFromDate(date) {
  return Moment(date, 'YYYY-MM-DD').isValid()
    ? Moment(date, 'YYYY-MM-DD').format('dddd')
    : '';
}

function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) {
    return '00:00';
  }
  const start = Moment(startTime, 'HH:mm');
  const end = Moment(endTime, 'HH:mm');
  if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
    return '00:00';
  }
  return Moment.utc(end.diff(start)).format('HH:mm');
}

export default function CreateSessionModal({
  show,
  tutorOptions,
  prefill,
  onCancel,
  onCreated,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!show) {
      return;
    }

    const fromDate = prefill?.date || '';
    const startHour =
      typeof prefill?.startHour === 'number'
        ? `${String(prefill.startHour).padStart(2, '0')}:00`
        : '';

    setForm({
      ...EMPTY_FORM,
      fk_tut_key: prefill?.tutorKey || '',
      sest_from_date: fromDate,
      sest_to_date: fromDate,
      sest_day: fromDate ? dayNameFromDate(fromDate) : '',
      sest_start_time: startHour,
      sest_end_time: startHour
        ? Moment(startHour, 'HH:mm').add(1, 'hour').format('HH:mm')
        : '',
      sest_max_student: '1',
    });
    setErrors({});
    setSubmitError('');
  }, [show, prefill]);

  const isOneOff = form.sest_is_holidays === '2';
  const duration = useMemo(
    () => calculateDuration(form.sest_start_time, form.sest_end_time),
    [form.sest_start_time, form.sest_end_time]
  );

  function updateField(name, value) {
    setForm((previous) => {
      const next = { ...previous, [name]: value };

      if (name === 'sest_from_date') {
        next.sest_day = dayNameFromDate(value);
        if (previous.sest_is_holidays === '2' || !previous.sest_to_date) {
          next.sest_to_date = value;
        }
      }

      if (name === 'sest_is_holidays' && value === '2') {
        next.sest_to_date = previous.sest_from_date;
        next.sest_skip_holidays = '';
      }

      return next;
    });
    setErrors((previous) => ({ ...previous, [name]: undefined }));
    setSubmitError('');
  }

  function validate() {
    const next = {};
    if (!form.fk_tut_key) next.fk_tut_key = 'Tutor is required.';
    if (!form.sest_type) next.sest_type = 'Session type is required.';
    if (!form.sest_is_holidays) next.sest_is_holidays = 'Session class is required.';
    if (!form.sest_from_date) next.sest_from_date = 'From date is required.';
    if (!form.sest_to_date) next.sest_to_date = 'To date is required.';
    if (!form.sest_day) next.sest_day = 'Day is required.';
    if (!form.sest_start_time) next.sest_start_time = 'Start time is required.';
    if (!form.sest_end_time) next.sest_end_time = 'End time is required.';
    if (
      form.sest_start_time &&
      form.sest_end_time &&
      !Moment(form.sest_end_time, 'HH:mm').isAfter(Moment(form.sest_start_time, 'HH:mm'))
    ) {
      next.sest_end_time = 'End time must be after start time.';
    }
    if (!form.sest_max_student || parseInt(form.sest_max_student, 10) < 1) {
      next.sest_max_student = 'Maximum students must be at least 1.';
    }
    if (form.sest_description && form.sest_description.length < 2) {
      next.sest_description = 'Minimum 2 characters is required.';
    }
    if (form.sest_description && form.sest_description.length > 40) {
      next.sest_description = 'Maximum 40 characters is required.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createSessionTime({
        fk_tut_key: form.fk_tut_key,
        sest_type: form.sest_type,
        sest_is_holidays: form.sest_is_holidays,
        sest_from_date: form.sest_from_date,
        sest_to_date: isOneOff ? form.sest_from_date : form.sest_to_date,
        sest_day: form.sest_day,
        sest_start_time: `${form.sest_start_time}:00`,
        sest_end_time: `${form.sest_end_time}:00`,
        sest_duration: '',
        sest_skip_holidays: isOneOff ? '' : form.sest_skip_holidays,
        sest_max_student: parseInt(form.sest_max_student, 10),
        sest_description: form.sest_description || '',
      });

      if (result.ok) {
        onCreated();
        return;
      }

      setSubmitError(result.message || 'Failure! Unable to create session. Try again later.');
    } catch (error) {
      console.error('Calendar: create session failed', error);
      setSubmitError('Failure! Unable to create session. Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Create Session Time</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">Tutor</h6>
              <Select
                menuPlacement="auto"
                options={tutorOptions}
                value={tutorOptions.find((option) => option.value === form.fk_tut_key) || null}
                onChange={(option) => updateField('fk_tut_key', option ? option.value : '')}
                placeholder="Select tutor"
              />
              {errors.fk_tut_key ? <span className="error">{errors.fk_tut_key}</span> : null}
            </Col>

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">Session type</h6>
              <Select
                menuPlacement="auto"
                options={SESSION_TYPE_OPTIONS}
                value={SESSION_TYPE_OPTIONS.find((option) => option.value === form.sest_type) || null}
                onChange={(option) => updateField('sest_type', option ? option.value : '')}
              />
              {errors.sest_type ? <span className="error">{errors.sest_type}</span> : null}
            </Col>

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">Class</h6>
              <Select
                menuPlacement="auto"
                options={options_isholiday}
                value={options_isholiday.find((option) => option.value === form.sest_is_holidays) || null}
                onChange={(option) => updateField('sest_is_holidays', option ? option.value : '')}
              />
              {errors.sest_is_holidays ? (
                <span className="error">{errors.sest_is_holidays}</span>
              ) : null}
            </Col>

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">Day</h6>
              <Select
                menuPlacement="auto"
                options={options_days_sessTime}
                value={
                  options_days_sessTime.find((option) => option.value === form.sest_day) || null
                }
                onChange={(option) => updateField('sest_day', option ? option.value : '')}
              />
              {errors.sest_day ? <span className="error">{errors.sest_day}</span> : null}
            </Col>

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">From date</h6>
              <Form.Control
                type="date"
                value={form.sest_from_date}
                onChange={(event) => updateField('sest_from_date', event.target.value)}
              />
              {errors.sest_from_date ? (
                <span className="error">{errors.sest_from_date}</span>
              ) : null}
            </Col>

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">To date</h6>
              <Form.Control
                type="date"
                value={form.sest_to_date}
                disabled={isOneOff}
                min={form.sest_from_date || undefined}
                onChange={(event) => updateField('sest_to_date', event.target.value)}
              />
              {errors.sest_to_date ? <span className="error">{errors.sest_to_date}</span> : null}
            </Col>

            <Col lg="4" className="mb-3">
              <h6 className="form-lable required-field">Start time</h6>
              <Form.Control
                type="time"
                value={form.sest_start_time}
                onChange={(event) => updateField('sest_start_time', event.target.value)}
              />
              {errors.sest_start_time ? (
                <span className="error">{errors.sest_start_time}</span>
              ) : null}
            </Col>

            <Col lg="4" className="mb-3">
              <h6 className="form-lable required-field">End time</h6>
              <Form.Control
                type="time"
                value={form.sest_end_time}
                onChange={(event) => updateField('sest_end_time', event.target.value)}
              />
              {errors.sest_end_time ? (
                <span className="error">{errors.sest_end_time}</span>
              ) : null}
            </Col>

            <Col lg="4" className="mb-3">
              <h6 className="form-lable">Duration</h6>
              <Form.Control type="text" value={duration} readOnly />
            </Col>

            {!isOneOff ? (
              <Col lg="6" className="mb-3">
                <h6 className="form-lable">Skip holidays</h6>
                <Select
                  menuPlacement="auto"
                  options={options_holidays}
                  value={
                    options_holidays.find((option) => option.value === form.sest_skip_holidays) ||
                    null
                  }
                  onChange={(option) =>
                    updateField('sest_skip_holidays', option ? option.value : '')
                  }
                />
              </Col>
            ) : null}

            <Col lg="6" className="mb-3">
              <h6 className="form-lable required-field">Max students</h6>
              <Form.Control
                type="number"
                min={1}
                value={form.sest_max_student}
                onChange={(event) => updateField('sest_max_student', event.target.value)}
              />
              {errors.sest_max_student ? (
                <span className="error">{errors.sest_max_student}</span>
              ) : null}
            </Col>

            <Col lg="12" className="mb-2">
              <h6 className="form-lable">Session description</h6>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={40}
                value={form.sest_description}
                onChange={(event) => updateField('sest_description', event.target.value)}
              />
              {errors.sest_description ? (
                <span className="error">{errors.sest_description}</span>
              ) : null}
            </Col>
          </Row>

          {submitError ? <div className="error mt-2">{submitError}</div> : null}
        </Modal.Body>

        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              className="primary-filled"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

CreateSessionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  tutorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  prefill: PropTypes.shape({
    tutorKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    date: PropTypes.string,
    startHour: PropTypes.number,
  }),
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

CreateSessionModal.defaultProps = {
  prefill: null,
};
